import { DefaultMap, dontAwait } from "@glideapps/ts-necessities";
import { definedMap, hasOwnProperty } from "collection-utils";
import type {
    Batch,
    DiffResults,
    DocumentData,
    Query,
    QueryResults,
    SortDirection,
    Transaction,
} from "common/Database";
import { Database, unzipString, zipString } from "common/Database";
import { appReadWriteCountsCollectionName, frontendAnalyticsCollectionName } from "common/database-strings";
import { logError, logInfo } from "common/debug-print";
import { getFeatureFlag } from "common/feature-flags";
import type { FeatureSettings } from "common/feature-settings";
import { getFeatureSetting, listenFeatureSettings, setFeatureSettings } from "common/feature-settings";
import type { LocationSettings } from "common/location-common";
import { getStaticAppConfig } from "common/static-app";
import type { JSONObject } from "common/Support";
import {
    assert,
    convertDateToTimeZoneAgnostic,
    exceptionToString,
    getISODateString,
    isArray,
    panic,
    sleep,
} from "common/Support";
import { featureSettingsStorageKey, localStorageGetItem, localStorageSetItem } from "support/local-storage";
import { SyncJobQueue } from "common/sync-job-queue";
import { frontendSendEvent, frontendTrace } from "common/tracing";
import firebase from "firebase/app";
import { deleteDB } from "idb";
import once from "lodash/once";
import throttle from "lodash/throttle";
import toPairs from "lodash/toPairs";
import { standalone } from "webapp/lib/device";
import { hasUsableSessionStorage, sessionStorageGetItem, sessionStorageSetItem } from "webapp/lib/session-storage";
import { shortUserAgent } from "./browser-detect";
import { isDocumentHidden, onVisibilityChange } from "./browser-hacks";
import { reloadBrowserWindow } from "./browser-reload";
import { initFirebase } from "./firebase";
import {
    addIDBOpenHook,
    forceIndexedDBReplacementIfNecessary,
    getIndexedDBOpenDatabase,
    isMissingObjectStoreError,
} from "./indexeddb-hacks";
import { LazyLoadError, lazyLoading } from "./lazy-loading";
import { getLocationSettings } from "./location";

function queryResultsFromSnapshots(docs: firebase.firestore.QueryDocumentSnapshot[]): QueryResults {
    return docs.map(doc => ({ data: doc.data(), id: doc.id, path: doc.ref.path }));
}

const sampleMultiplier = 10;

class WebDatabaseAccessCounter {
    private readonly _increments: DefaultMap<string, number> = new DefaultMap(() => 0);

    constructor(private readonly _db: Database, private readonly _docID: string) {}

    private register(prefix: string, collectionName: string, count: number): void {
        function inc(x: number) {
            return x + count;
        }

        try {
            collectionName = collectionName.split("/")[0];
            if (collectionName.startsWith(appReadWriteCountsCollectionName)) return;

            this._increments.update(`${prefix}:${collectionName}`, inc);
            this._increments.update("total", inc);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.flushThrottled();
        } catch (e: unknown) {
            logError("Could not count access", e);
        }
    }

    public registerReads(collectionName: string, count: number): void {
        this.register("read", collectionName, count);
    }

    public registerWrites(collectionName: string, count: number): void {
        this.register("write", collectionName, count);
    }

    private flush = async () => {
        const updates: DocumentData = {};
        for (const [k, v] of this._increments.entries()) {
            if (v <= 0) continue;
            updates[k] = this._db.incrementFieldValue(v * sampleMultiplier);
        }
        this._increments.clear();
        if (Object.keys(updates).length < 1) return;

        updates.numUpdates = this._db.incrementFieldValue(sampleMultiplier);
        updates.numUpdatesRaw = this._db.incrementFieldValue(1);

        if (Math.random() <= 1 / sampleMultiplier) {
            await this._db.updateDocument(
                frontendAnalyticsCollectionName(getISODateString(new Date())),
                this._docID,
                updates
            );
        }
    };

    private flushThrottled = throttle(this.flush, 3000);
}

class WebDatabaseBatch implements Batch {
    private _batch: firebase.firestore.WriteBatch;
    private _count: number = 0;

    constructor(public readonly database: WebDatabase, private readonly _db: firebase.firestore.Firestore) {
        this._batch = this._db.batch();
    }

    private async commit(): Promise<void> {
        if (this._count === 0) return;

        await this._batch.commit();
        this._batch = this._db.batch();
        this._count = 0;
    }

    private async operationAdded(collectionName: string): Promise<void> {
        this._count += 1;
        this.database.accessCounter?.registerWrites(collectionName, 1);
        if (this._count >= 500) {
            await this.commit();
        }
    }

    public async setDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string> {
        const collection = this._db.collection(collectionName);
        const doc = id === undefined ? collection.doc() : collection.doc(id);

        this._batch.set(doc, data);
        await this.operationAdded(collectionName);

        return doc.id;
    }

    public async deleteDocument(collectionName: string, id: string): Promise<void> {
        const doc = this._db.collection(collectionName).doc(id);

        this._batch.delete(doc);
        await this.operationAdded(collectionName);
    }

    public async updateDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string> {
        const collection = this._db.collection(collectionName);
        const doc = id === undefined ? collection.doc() : collection.doc(id);

        this._batch.set(doc, data, { merge: true });
        await this.operationAdded(collectionName);

        return doc.id;
    }

    public async updateDocumentWithNesting(collectionName: string, id: string, data: DocumentData): Promise<void> {
        const doc = this._db.collection(collectionName).doc(id);
        this._batch.update(doc, data);
        await this.operationAdded(collectionName);
    }

    public async finish(): Promise<void> {
        await this.commit();
    }
}

class WebDatabaseTransaction implements Transaction {
    constructor(
        public readonly database: WebDatabase,
        private readonly _db: firebase.firestore.Firestore,
        private readonly _transaction: firebase.firestore.Transaction
    ) {}

    public async getDocument(collectionName: string, id: string): Promise<DocumentData | undefined> {
        const doc = this._db.collection(collectionName).doc(id);
        const snapshot = await this._transaction.get(doc);
        this.database.accessCounter?.registerReads(collectionName, 1);
        return snapshot.data();
    }

    public async setDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string> {
        const collection = this._db.collection(collectionName);
        const doc = id === undefined ? collection.doc() : collection.doc(id);

        this._transaction.set(doc, data);
        this.database.accessCounter?.registerWrites(collectionName, 1);
        return doc.id;
    }

    public async deleteDocument(collectionName: string, id: string): Promise<void> {
        const doc = this._db.collection(collectionName).doc(id);
        this._transaction.delete(doc);
    }

    public async updateDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string> {
        const collection = this._db.collection(collectionName);
        const doc = id === undefined ? collection.doc() : collection.doc(id);

        this._transaction.set(doc, data, { merge: true });
        this.database.accessCounter?.registerWrites(collectionName, 1);
        return doc.id;
    }

    public async updateDocumentWithNesting(collectionName: string, id: string, data: DocumentData): Promise<void> {
        const doc = this._db.collection(collectionName).doc(id);
        this._transaction.update(doc, data);
        this.database.accessCounter?.registerWrites(collectionName, 1);
    }
}

let firestore: firebase.firestore.Firestore | undefined;
let firestoreLoad: Promise<any> | undefined;

const persistenceDBName = `firestore/[DEFAULT]/${getLocationSettings().firebaseConfig.projectId}/main`;

const persistenceFailuresKey = "glide-firestore-persistence-failures";
const maxConsecutivePersistenceFailures = 2;

function injectPersistenceMutexFault() {
    const idb = getIndexedDBOpenDatabase(persistenceDBName);
    if (idb === undefined) {
        logError(`No open database at ${persistenceDBName}`);
        return;
    }

    const txn = idb.transaction("owner", "readwrite");
    const txnStore = txn.objectStore("owner");
    const getReq = txnStore.get("owner");
    getReq.onsuccess = () => {
        const priorOwner = getReq.result?.ownerId;
        if (typeof priorOwner !== "string") {
            logError(`Unexpected transaction get result`, getReq.result);
            return;
        }

        txnStore.put(
            {
                ownerId: priorOwner.split("").reverse().join(""),
                allowTabSynchronization: false,
                leaseTimestampMs: Date.now(),
            },
            "owner"
        );
        txn.oncomplete = () => {
            logError("Injected Firestore Persistence mutex fault");
        };
    };
    txn.onerror = () => {
        logError(`Could not inject Persistence mutex fault`, txn.error);
    };
}

async function initFirestore(): Promise<firebase.firestore.Firestore> {
    firestoreLoad = await lazyLoading("firestore", false, () => import("firebase/firestore"));
    forceIndexedDBReplacementIfNecessary(window);

    // Note that we still have to load the Firestore libraries even when
    // we're not using them. Too many code paths assume they are loaded.
    if (getStaticAppConfig() !== undefined) {
        // If we're in static mode, we do not use Firestore.
        // Instead, we'll return a never-resolving Promise to block
        // all Firestore activity without incurring any API changes.
        return new Promise(_resolve => {
            // This method intentionally left blank
        });
    }

    initFirebase();

    // Initialize Cloud Firestore through Firebase
    firestore = firebase.firestore();

    firestore.settings({
        // Versions of Kaspersky Endpoint Security do not function
        // properly with Firestore. This forces the Firestore SDK
        // to try and work around bad HTTP proxy behavior.
        //
        // See https://github.com/firebase/firebase-js-sdk/issues/1190#issuecomment-552860520
        experimentalForceLongPolling: true,
        // https://github.com/quicktype/glide/issues/8941
        ignoreUndefinedProperties: true,
    });

    let canUsePersistence =
        ((getFeatureSetting("firestorePersistenceInPWA") && standalone) || getFeatureFlag("useFirestorePersistence")) &&
        hasUsableSessionStorage();

    if (canUsePersistence) {
        try {
            const priorPersistenceFailuresString = sessionStorageGetItem(persistenceFailuresKey);
            if (priorPersistenceFailuresString !== undefined) {
                const priorPersistenceFailures = Number.parseFloat(priorPersistenceFailuresString);
                if (priorPersistenceFailures >= maxConsecutivePersistenceFailures) {
                    frontendSendEvent("firestore persistence failure", 0, {
                        failures: priorPersistenceFailures,
                        permanent: true,
                        userAgent: shortUserAgent,
                    });
                    logError("Cannot use Firestore Persistence: too many consecutive failures");
                    canUsePersistence = false;
                }
            }
        } catch (e: unknown) {
            frontendSendEvent("firestore persistence failure", 0, {
                exception: exceptionToString(e),
                permanent: true,
                userAgent: shortUserAgent,
            });
            logError(`Could not read sessionStorage for Firestore Persistence: ${exceptionToString(e)}`);
            canUsePersistence = false;
        }
    }

    if (canUsePersistence) {
        // There's no reason to believe that Safari is polite.
        forceIndexedDBReplacementIfNecessary(window);

        await firestore.enablePersistence();
        // This ought to be a no-op but the Persistence Mutex failure is only visible
        // on the _next_ enqueued item.
        await firestore.enableNetwork();
        // If we did enable persistence, we should be able to inject a mutex
        // fault to test our recovery.
        (window as any)._injectPersistenceMutexFault = () => injectPersistenceMutexFault();
    }

    return firestore;
}

export async function waitForFirestoreLibraries(): Promise<void> {
    if (firestorePromise === undefined) {
        firestorePromise = initFirestore();
    }
    while (firestoreLoad === undefined) {
        await sleep(1);
    }
    await firestoreLoad;
}

function isPermissionsError(e: unknown): e is { code: "permission-denied" } {
    return hasOwnProperty(e, "code") && e.code === "permission-denied";
}

function isInternalAssertionError(e: unknown): e is { message: string } {
    return (
        hasOwnProperty(e, "message") &&
        typeof e.message === "string" &&
        e.message.startsWith("FIRESTORE") &&
        e.message.indexOf(" INTERNAL ASSERTION FAILED:") > 0
    );
}

function isPersistenceExclusivityError(e: unknown): e is { code: "failed-precondition" } {
    return (
        hasOwnProperty(e, "code") &&
        e.code === "failed-precondition" &&
        hasOwnProperty(e, "message") &&
        typeof e.message === "string" &&
        e.message.startsWith("Failed to obtain exclusive access to the persistence layer.")
    );
}

function isTerminatedClientError(e: unknown): e is { code: "failed-precondition" } {
    return (
        hasOwnProperty(e, "code") &&
        e.code === "failed-precondition" &&
        hasOwnProperty(e, "message") &&
        typeof e.message === "string" &&
        e.message.startsWith("The client has already been terminated.")
    );
}

function isPersistenceUnimplementedError(e: unknown): e is { code: "unimplemented" } {
    return (
        hasOwnProperty(e, "code") &&
        e.code === "unimplemented" &&
        hasOwnProperty(e, "message") &&
        typeof e.message === "string" &&
        e.message.indexOf("Offline persistence has been disabled.") >= 0
    );
}

const postDeletionReloadWaitFactor = 1.4;
const postDeletionReloadWaitBasis = 2000;

let handledPersistenceFailure = false;

// The Firestore Persistence mutex is super buggy and regularly
// falsely rejects lease grabbing. We're lucky: we only enable it
// in standalone mode, so we _know_ for a fact this is completely
// unnecessary. So what we'll do is just remove the record every
// time we start up.
async function trapIndexedDBMutex(db: IDBDatabase) {
    if (!standalone) return;
    if (db.name !== persistenceDBName) return;

    try {
        const txn = db.transaction(["owner"], "readwrite");
        const store = txn.objectStore("owner");
        store.delete("owner");

        return await new Promise<void>((resolve, reject) => {
            txn.onerror = reject;
            txn.oncomplete = () => resolve();
        });
    } catch (e: unknown) {
        // Someone actually cares if we don't have an "owner" store.
        // But not this particular bit of code. It's not our job here
        // to handle this.
        //
        // In fact, the error traps elsewhere in this file handle this fun
        // little bit of surprise database corruption.
        if (isMissingObjectStoreError(e)) return;
        throw e;
    }
}

addIDBOpenHook(trapIndexedDBMutex);

async function performPersistenceErrorRecovery(e: unknown): Promise<never> {
    if (handledPersistenceFailure) {
        return new Promise(() => {
            // Do nothing; this is a permanently stalled Promise.
            // Anyone trying to handle the persistence failure needs to wait
            // for a full page reload now.
        });
    }

    if (window.indexedDB === undefined) throw e;

    let priorFailures: number = 0;
    try {
        const priorFailuresString = sessionStorageGetItem(persistenceFailuresKey);
        if (priorFailuresString === undefined) {
            sessionStorageSetItem(persistenceFailuresKey, "1");
        } else {
            try {
                priorFailures = Number.parseFloat(priorFailuresString);
                sessionStorageSetItem(persistenceFailuresKey, (priorFailures + 1).toString());
            } catch {
                sessionStorageSetItem(persistenceFailuresKey, maxConsecutivePersistenceFailures.toString());
            }
        }
    } catch {
        // This is unfortunate but there's nothing to be done here.
    }

    handledPersistenceFailure = true;
    frontendSendEvent("firestore persistence failure", 0, {
        exception: exceptionToString(e),
        permanent: false,
        userAgent: shortUserAgent,
    });
    const failureIsPermanent = priorFailures + 1 >= maxConsecutivePersistenceFailures;

    if (!isPersistenceExclusivityError(e) || failureIsPermanent) {
        try {
            await deleteDB(persistenceDBName);
        } catch (x: unknown) {
            logError("Exception occurred trying to delete Firestore database", exceptionToString(x));
            // Yep, we're throwing the original exception here.
            // If it weren't for this shim we would have thrown it anyway.
            throw e;
        }
    }

    // We don't want to sleep at all on non-permanent Persistence failures,
    // we can just reload and let the mutex deletion code recover quickly.
    let sleepTime = isPersistenceExclusivityError(e) && !failureIsPermanent ? 0 : postDeletionReloadWaitBasis;
    let reloadCount = 0;
    while (true) {
        // We sleep before reloading to attempt to send data to Honeycomb.
        // The attempt to Honeycomb should be allowed to fail, and shouldn't
        // interfere with the reload process; under normal circumstances the
        // network request should go through quickly.
        await sleep(sleepTime);
        if (sleepTime === 0) {
            // Except, if we want to retry a reload, we'll need to reset the sleep time
            // to something sane.
            sleepTime = postDeletionReloadWaitBasis;
        }
        sleepTime *= postDeletionReloadWaitFactor;

        if (!getFeatureFlag("debugBrowserReload") || reloadCount < 1) {
            reloadBrowserWindow("Attempting to recover from persistence failure", exceptionToString(e));
        }
        reloadCount++;
    }
}

function isTrappableIndirectFirestoreError(e: unknown): boolean {
    // We used to think that isTerminatedClientError was recoverable...
    // but it's not. Firestore gets hard-stuck on it.
    return (
        isInternalAssertionError(e) ||
        isPersistenceExclusivityError(e) ||
        isPersistenceUnimplementedError(e) ||
        isTerminatedClientError(e)
    );
}

async function trapDirectPersistenceFailure(e: unknown): Promise<never> {
    if (!isTrappableIndirectFirestoreError(e) && !isMissingObjectStoreError(e)) throw e;

    return await performPersistenceErrorRecovery(e);
}

window.addEventListener("error", ev => {
    // Firestore will sometimes leak uncaught INTERNAL ASSERTION FAILED exceptions;
    // when it does, we're dumping persistence and trying again.
    //
    // Firestore Persistence also ends up in unrecoverable deadlock far too often.
    // When it does, we're waiting 6 seconds and trying again. If it happens too
    // often then we shut persistence off.
    //
    // This one is for inline execution, but doesn't handle promises.
    if (isTrappableIndirectFirestoreError(ev.error)) {
        ev.preventDefault();
        ev.stopPropagation();
        dontAwait(performPersistenceErrorRecovery(ev.error));
    }
});

window.addEventListener("unhandledrejection", ev => {
    // Firestore will sometimes leak uncaught INTERNAL ASSERTION FAILED exceptions;
    // when it does, we're dumping persistence and trying again.
    //
    // Firestore Persistence also ends up in unrecoverable deadlock far too often.
    // When it does, we're waiting 6 seconds and trying again. If it happens too
    // often then we shut persistence off.
    //
    // This one is for promises, but doesn't handle inline execution.
    if (isTrappableIndirectFirestoreError(ev.reason)) {
        ev.preventDefault();
        ev.stopPropagation();
        dontAwait(performPersistenceErrorRecovery(ev.reason));
    }
});

let firestorePromise: Promise<firebase.firestore.Firestore> | undefined;

async function getFirestore(): Promise<firebase.firestore.Firestore> {
    if (firestore !== undefined) return firestore;
    if (firestorePromise === undefined) {
        firestorePromise = initFirestore();
    }
    return firestorePromise;
}

function trySaveFeatureSettings(featureSettings: FeatureSettings) {
    setFeatureSettings(featureSettings);
    localStorageSetItem(featureSettingsStorageKey, zipString(JSON.stringify(featureSettings)));
}

// The feature settings are global to the entire running instance.
// We don't need to get them more than once per instance, and it really
// doesn't matter which analytics bucket they end up in. Hence the `once`.
const initFeatureSettings = once((db: Database) => {
    try {
        const storedFeatureSettings =
            definedMap(localStorageGetItem(featureSettingsStorageKey), s => JSON.parse(unzipString(s))) ??
            (hasOwnProperty(window, "glideFeatureSettings") ? window.glideFeatureSettings : undefined);
        if (storedFeatureSettings !== undefined) {
            trySaveFeatureSettings(storedFeatureSettings as FeatureSettings);
        }
    } catch (e: unknown) {
        logError(`Could not load saved feature settings: ${e}`);
    }
    listenFeatureSettings(db, trySaveFeatureSettings);
});

interface ShutdownHandler {
    enterNetworkConnectionLock(): Promise<void>;
    exitNetworkConnectionLock(): void;
}

class DummyShutdownHandler implements ShutdownHandler {
    public async enterNetworkConnectionLock(): Promise<void> {
        return;
    }

    public exitNetworkConnectionLock(): void {
        return;
    }
}

class WebDatabaseShutdownHandler implements ShutdownHandler {
    private outstandingLocks: number = 0;
    private networkShutdownResolver: (() => void) | undefined;

    private consumerWaitResolvers: (() => void)[] = [];

    private networkShutdown: boolean = false;
    private shutdownQueue: SyncJobQueue = new SyncJobQueue();

    private db = getFirestore();

    constructor() {
        // Chrome on Android had bugs where the visibility transition
        // events would not fire, locking up this shutdown handler until
        // the app was force restarted. Hence the incessant polling.

        // eslint-disable-next-line glide/no-set-interval-without-assign
        setInterval(() => this.handleVisibilityTransition(false), 500);
        onVisibilityChange(this.handleVisibilityTransition);
    }

    // Note that this should be run via the shutdown queue, so that
    // all calls are serialized.
    private async shutdownNetworkWaitingForConsumers(): Promise<void> {
        if (this.networkShutdown) return;

        await new Promise<void>(resolve => {
            const setNetworkShutdownFlagAndResolve = () => {
                this.networkShutdown = true;
                resolve();
            };
            assert(this.networkShutdownResolver === undefined, "Pre-existing shutdown hard lock resolver");

            if (this.outstandingLocks > 0) {
                this.networkShutdownResolver = setNetworkShutdownFlagAndResolve;
            } else {
                setNetworkShutdownFlagAndResolve();
            }
        });

        await (await this.db).disableNetwork();
    }

    // Note that this should be run via the shutdown queue, so that
    // all calls are serialized.
    private async enableNetworkReleasingConsumers() {
        if (!this.networkShutdown) return;

        await (await this.db).enableNetwork();
        this.networkShutdown = false;
        const resolvers = this.consumerWaitResolvers.splice(0, this.consumerWaitResolvers.length);
        for (const resolve of resolvers) {
            try {
                resolve();
            } catch (e: unknown) {
                logError("Error calling resolver", e);
            }
        }
    }

    private handleVisibilityTransition = (logChange: boolean = true) => {
        if (logChange) {
            logInfo("Shutdown handler visibility change", isDocumentHidden(), Date.now());
        }
        this.shutdownQueue
            .run(async () => {
                try {
                    if (!this.networkShutdown && isDocumentHidden()) {
                        logInfo("Shutdown handler enter network shutdown", Date.now());
                        await this.shutdownNetworkWaitingForConsumers();
                        logInfo("Shutdown handler exit network shutdown", Date.now());
                    }
                    // This is two ifs instead of one if/else because the document
                    // might become unhidden while awaiting the prior call.
                    // Just breaking this up into two ifs keeps the expected behavior
                    // of one held transition, but also lets us quickly handle the
                    // not -> visible transition before we even get to the appropriate
                    // queue entry.
                    if (this.networkShutdown && !isDocumentHidden()) {
                        logInfo("Shutdown handler enter network restore", Date.now());
                        await this.enableNetworkReleasingConsumers();
                        logInfo("Shutdown handler exit network restore", Date.now());
                    }
                } catch (e: unknown) {
                    logError(`Could not handle visibility transition: ${e}`);
                    try {
                        await trapDirectPersistenceFailure(e);
                    } catch {
                        // If we're catching a thrown Error here, odds are
                        // the error we caught in the block above isn't a
                        // known Persistence crash. We definitely want to
                        // know about this, but can't do anything about it yet.

                        // If the document is hidden, it's very likely we're actually
                        // closing the page here. We don't need to report that.
                        if (e instanceof LazyLoadError && isDocumentHidden()) return;

                        frontendSendEvent("firestore network transition failure", 0, {
                            exception: exceptionToString(e),
                            userAgent: shortUserAgent,
                        });
                    }
                }
            })
            .catch(() => {
                // This empty block is only here to make ESLint happy
            });
    };

    public enterNetworkConnectionLock(): Promise<void> {
        return new Promise(resolve => {
            const incrementOutstandingAndResolve = () => {
                this.outstandingLocks++;
                resolve();
            };
            if (this.networkShutdown) {
                this.consumerWaitResolvers.push(incrementOutstandingAndResolve);
            } else {
                incrementOutstandingAndResolve();
            }
        });
    }

    public exitNetworkConnectionLock(): void {
        this.outstandingLocks--;
        assert(this.outstandingLocks >= 0, "Unbalanced network hard lock exit");

        if (this.outstandingLocks === 0) {
            const { networkShutdownResolver } = this;
            this.networkShutdownResolver = undefined;
            if (networkShutdownResolver !== undefined) {
                networkShutdownResolver();
            }
        }
    }
}

const useShutdownHandler = true;
let globalShutdownHandler: ShutdownHandler | undefined;

function getGlobalShutdownHandler(): ShutdownHandler {
    if (globalShutdownHandler === undefined) {
        if (useShutdownHandler) {
            globalShutdownHandler = new WebDatabaseShutdownHandler();
        } else {
            globalShutdownHandler = new DummyShutdownHandler();
        }
    }
    return globalShutdownHandler;
}

export class WebDatabase extends Database {
    public readonly accessCounter: WebDatabaseAccessCounter | undefined;

    constructor(countsDocID: string | undefined) {
        super();

        getGlobalShutdownHandler(); // For side-effects: we want a shutdown handler as soon as we get a WebDatabase
        this.accessCounter = definedMap(countsDocID, id => new WebDatabaseAccessCounter(this, id));
        initFeatureSettings(this);
    }

    private async getDB(): Promise<firebase.firestore.Firestore> {
        return getFirestore();
    }

    public get projectID(): string {
        return panic("projectID shouldn't be called in WebDatabase");
    }

    public isAvailable(): boolean {
        return getStaticAppConfig() === undefined;
    }

    public async enterNetworkConnectionLock(): Promise<void> {
        return await getGlobalShutdownHandler().enterNetworkConnectionLock();
    }

    public get deploymentLocationSettings(): LocationSettings {
        return getLocationSettings();
    }

    public exitNetworkConnectionLock(): void {
        getGlobalShutdownHandler().exitNetworkConnectionLock();
    }

    public get deleteFieldValue(): unknown {
        return firebase.firestore.FieldValue.delete();
    }

    public get serverTimestampFieldValue(): unknown {
        return firebase.firestore.FieldValue.serverTimestamp();
    }

    public arrayUnionFieldValue(...values: unknown[]): unknown {
        return firebase.firestore.FieldValue.arrayUnion(...values);
    }

    public arrayRemoveFieldValue(...values: unknown[]): unknown {
        return firebase.firestore.FieldValue.arrayRemove(...values);
    }

    public incrementFieldValue(increment: number = 1): unknown {
        return firebase.firestore.FieldValue.increment(increment);
    }

    public async makeDocumentID(): Promise<string> {
        const collection = (await this.getDB()).collection("dummy");
        return collection.doc().id;
    }

    public async getDocument(collectionName: string, id: string): Promise<DocumentData | undefined> {
        await this.enterNetworkConnectionLock();
        try {
            return await frontendTrace("getDocument", { collection: collectionName }, async () => {
                const doc = await (await this.getDB()).collection(collectionName).doc(id).get();
                this.accessCounter?.registerReads(collectionName, 1);
                return doc.data();
            });
        } catch (e: unknown) {
            return await trapDirectPersistenceFailure(e);
        } finally {
            this.exitNetworkConnectionLock();
        }
    }

    private async makeQuery(
        collectionName: string,
        queries: ReadonlyArray<Query>,
        sortFieldPath?: string,
        sortDirection?: SortDirection,
        limit?: number
    ): Promise<firebase.firestore.Query> {
        let query: firebase.firestore.Query = (await this.getDB()).collection(collectionName);
        if (sortFieldPath !== undefined) {
            query = query.orderBy(sortFieldPath, sortDirection);
        }
        for (const { fieldPath, opString, value } of queries) {
            query = query.where(fieldPath, opString, value);
        }
        if (limit !== undefined) {
            query = query.limit(limit);
        }
        return query;
    }

    public async getDocumentsWhere(
        collectionName: string,
        queries: ReadonlyArray<Query>,
        sortFieldPath?: string,
        sortDirection?: SortDirection,
        limit?: number
    ): Promise<QueryResults> {
        const query = await this.makeQuery(collectionName, queries, sortFieldPath, sortDirection, limit);
        await this.enterNetworkConnectionLock();
        try {
            return await frontendTrace("getDocumentsWhere", { collection: collectionName }, async () => {
                const docs = (await query.get()).docs;
                this.accessCounter?.registerReads(collectionName, docs.length);
                return docs.map(doc => ({ data: doc.data(), id: doc.id, path: doc.ref.path }));
            });
        } catch (e: unknown) {
            return await trapDirectPersistenceFailure(e);
        } finally {
            this.exitNetworkConnectionLock();
        }
    }

    public getDocumentsWherePaginated(): Promise<void> {
        return panic("Not supported in WebDatabase");
    }

    public async setDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string> {
        await this.enterNetworkConnectionLock();
        try {
            return await frontendTrace("setDocument", { collection: collectionName }, async () => {
                const collection = (await this.getDB()).collection(collectionName);
                const doc = id === undefined ? collection.doc() : collection.doc(id);
                await doc.set(data);
                this.accessCounter?.registerWrites(collectionName, 1);
                return doc.id;
            });
        } catch (e: unknown) {
            return await trapDirectPersistenceFailure(e);
        } finally {
            this.exitNetworkConnectionLock();
        }
    }

    public async deleteDocument(collectionName: string, id: string): Promise<void> {
        await this.enterNetworkConnectionLock();
        try {
            return await frontendTrace("deleteDocument", { collection: collectionName }, async () => {
                await (await this.getDB()).collection(collectionName).doc(id).delete();
                this.accessCounter?.registerWrites(collectionName, 1);
            });
        } catch (e: unknown) {
            return await trapDirectPersistenceFailure(e);
        } finally {
            this.exitNetworkConnectionLock();
        }
    }

    public async updateDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string> {
        await this.enterNetworkConnectionLock();
        try {
            return await frontendTrace("updateDocument", { collection: collectionName }, async () => {
                const collection = (await this.getDB()).collection(collectionName);
                const doc = id === undefined ? collection.doc() : collection.doc(id);
                await doc.set(data, { merge: true });
                this.accessCounter?.registerWrites(collectionName, 1);
                return doc.id;
            });
        } catch (e: unknown) {
            return await trapDirectPersistenceFailure(e);
        } finally {
            this.exitNetworkConnectionLock();
        }
    }

    public async updateDocumentWithNesting(collectionName: string, id: string, data: DocumentData): Promise<void> {
        await this.enterNetworkConnectionLock();
        try {
            return await frontendTrace("updateDocumentWithNesting", { collection: collectionName }, async () => {
                const doc = (await this.getDB()).collection(collectionName).doc(id);
                this.accessCounter?.registerWrites(collectionName, 1);
                await doc.update(data);
            });
        } catch (e: unknown) {
            return await trapDirectPersistenceFailure(e);
        } finally {
            this.exitNetworkConnectionLock();
        }
    }

    public async runBatch<T>(name: string, f: (b: Batch) => Promise<T>): Promise<T> {
        const batch = new WebDatabaseBatch(this, await this.getDB());
        await this.enterNetworkConnectionLock();
        return await frontendTrace(name, undefined, async () => {
            try {
                return await f(batch);
            } catch (e: unknown) {
                return await trapDirectPersistenceFailure(e);
            } finally {
                try {
                    await batch.finish();
                } finally {
                    this.exitNetworkConnectionLock();
                }
            }
        });
    }

    public async runTransaction<T>(name: string, f: (t: Transaction) => Promise<T>): Promise<T> {
        const db = await this.getDB();
        await this.enterNetworkConnectionLock();
        return await frontendTrace(name, undefined, async () => {
            try {
                return await db.runTransaction(async t => f(new WebDatabaseTransaction(this, db, t)));
            } catch (e: unknown) {
                return await trapDirectPersistenceFailure(e);
            } finally {
                this.exitNetworkConnectionLock();
            }
        });
    }

    public listenToDocument(
        collectionName: string,
        id: string,
        onUpdate: (data: DocumentData) => void,
        onError: ((e: Error) => void) | undefined
    ): () => void {
        if (onError === undefined) {
            onError = e => {
                const permError = isPermissionsError(e);
                const logFunc = permError ? logInfo : logError;
                logFunc("Error listening to document", collectionName, id, e);
                if (!permError) {
                    dontAwait(trapDirectPersistenceFailure(e));
                }
            };
        }

        const ongoing = this.getDB().then(db =>
            db
                .collection(collectionName)
                .doc(id)
                .onSnapshot(snapshot => {
                    const data = snapshot.data();
                    if (data === undefined) return;
                    this.accessCounter?.registerReads(collectionName, 1);
                    onUpdate(data);
                }, onError)
        );
        return () => {
            ongoing.then(cancel => cancel()).catch(onError);
        };
    }

    public listenWhere(
        collectionName: string,
        queries: Query[],
        sortFieldPath: string | undefined,
        sortDirection: SortDirection | undefined,
        limit: number | undefined,
        onUpdate: (results: QueryResults) => void,
        onError?: (e: Error) => void
    ): () => void {
        if (onError === undefined) {
            onError = e => {
                logError("Error listening to query", collectionName, e);
                if (!isPermissionsError(e)) {
                    dontAwait(trapDirectPersistenceFailure(e));
                }
            };
        }

        const ongoing = this.makeQuery(collectionName, queries, sortFieldPath, sortDirection, limit).then(query =>
            query.onSnapshot(snapshot => {
                const changes = snapshot.docChanges();
                this.accessCounter?.registerReads(collectionName, changes.length);
                onUpdate(queryResultsFromSnapshots(snapshot.docs));
            }, onError)
        );
        return () => {
            ongoing.then(cancel => cancel()).catch(onError);
        };
    }

    public listenDiffWhere(
        collectionName: string,
        queries: Query[],
        sortFieldPath: string | undefined,
        sortDirection: SortDirection | undefined,
        onUpdate: (results: DiffResults) => void,
        onError: () => void
    ): () => void {
        const ongoing = this.makeQuery(collectionName, queries, sortFieldPath, sortDirection).then(query =>
            query.onSnapshot(
                snapshot => {
                    const changes = snapshot.docChanges();
                    this.accessCounter?.registerReads(collectionName, changes.length);
                    onUpdate(changes.map(({ type, doc }) => ({ kind: type, id: doc.id, data: doc.data() })));
                },
                e => {
                    logError("Error listening to diff query", collectionName, e);
                    onError();
                    // recoverFromPersistenceFailure will re-throw, but in here we don't care
                    // about the re-thrown error.
                    trapDirectPersistenceFailure(e).finally(() =>
                        logError("Attempted recovery from diff query error", collectionName, e)
                    );
                }
            )
        );
        return () => {
            ongoing.then(cancel => cancel()).catch(logError);
        };
    }

    public dateFromTimestamp(timestamp: unknown): Date {
        // sometimes(?) Firebase returns a timestamp as a Date
        if (timestamp instanceof Date) return timestamp;
        return (timestamp as firebase.firestore.Timestamp).toDate();
    }

    public convertDocumentProperty(x: unknown): unknown {
        if (x instanceof firebase.firestore.Timestamp) {
            return (x as firebase.firestore.Timestamp).toDate();
        }
        // FIXME: This doesn't really belong in here.  It should be somewhere
        // in `firestore-datastore.ts`, except that right now we special-case
        // loading comments, and that's somewhere else.  It's also duplicated
        // across both `Database` implementations.
        if (hasOwnProperty(x, "$dateTime")) {
            let dateTime = this.convertDocumentProperty(x.$dateTime);
            if (dateTime instanceof Date) {
                if (hasOwnProperty(x, "$isTimeZoneAgnostic") && x.$isTimeZoneAgnostic !== true) {
                    dateTime = convertDateToTimeZoneAgnostic(dateTime);
                }
                return dateTime;
            }
        }
        return x;
    }

    private convertValueFromDocument(v: unknown): unknown {
        assert(v !== undefined);
        if (isArray(v)) {
            return v.map(x => this.convertValueFromDocument(x));
        } else if (v instanceof firebase.firestore.Timestamp) {
            return v.toDate();
        } else if (typeof v === "object" && v !== null) {
            return this.convertFromDocument(v);
        } else if (
            v === null ||
            typeof v === "string" ||
            typeof v === "number" ||
            typeof v === "boolean" ||
            v instanceof Date
        ) {
            return v;
        } else {
            return panic(`Invalid value in Firestore document ${v}`);
        }
    }

    public convertFromDocument(data: DocumentData): JSONObject {
        const result: DocumentData = {};
        for (const [k, v] of toPairs(data)) {
            if (v === undefined) continue;
            result[k] = this.convertValueFromDocument(v);
        }
        return result;
    }

    public convertToDocument(_data: DocumentData): DocumentData {
        return panic("Not implemented");
    }
}

const builderAppDatabases = new DefaultMap<string, WebDatabase>(appID => new WebDatabase(`builder-app-${appID}`));
const builderUserDatabases = new DefaultMap<string, WebDatabase>(userID => new WebDatabase(`builder-user-${userID}`));
const playerDatabases = new DefaultMap<string, WebDatabase>(appID => new WebDatabase(`player-app-${appID}`));

export function getBuilderAppDatabase(appID: string): WebDatabase {
    return builderAppDatabases.get(appID);
}

export function getBuilderUserDatabase(userID: string): WebDatabase {
    return builderUserDatabases.get(userID);
}

export function getPlayerDatabase(appID: string): WebDatabase {
    return playerDatabases.get(appID);
}

let unaffiliatedDatabase: WebDatabase | undefined;

export function getUnaffiliatedDatabase(): WebDatabase {
    if (unaffiliatedDatabase === undefined) {
        unaffiliatedDatabase = new WebDatabase("unaffiliated");
    }
    return unaffiliatedDatabase;
}
